import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { type IBudgetTemplateAiImportResult,aiImportBudgetTemplate } from 'services'
import { Button, Input2, Modal } from 'stories/components'

type Props = {
  isOpen: boolean
  existingTitles: string[]
  onClose: () => void
  onApply: (title: string, result: IBudgetTemplateAiImportResult) => Promise<void> | void
}

/**
 * Builds a new budget template from a spreadsheet. The backend reads the sheet and returns a
 * preview; nothing is saved until the admin accepts it here and saves the configuration.
 */
export const BudgetTemplateAiImportModal = ({ isOpen, existingTitles, onClose, onApply }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [title, setTitle] = useState('')
  const [result, setResult] = useState<IBudgetTemplateAiImportResult | null>(null)

  const lineCount = result ? result.sections.reduce((count, section) => count + section.items.length, 0) : 0
  const duplicateTitle = existingTitles.some((v) => v.trim().toLowerCase() === title.trim().toLowerCase())

  const reset = () => {
    setLoading(false)
    setFileName('')
    setTitle('')
    setResult(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const runImport = async (file: File) => {
    setLoading(true)
    setResult(null)
    setFileName(file.name)
    try {
      const res = await aiImportBudgetTemplate(file)
      if (res?.success && res.data?.sections?.length) {
        setResult(res.data)
        // Seed the name from the file so the admin usually just accepts it.
        if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''))
      } else {
        toast(res?.message || 'No budget sections or line items were found in the spreadsheet.', { type: 'error' })
      }
    } catch {
      // Distinct from the message above: this one means the request never got an answer, which is
      // worth telling apart from the server reading the sheet and finding nothing in it.
      toast('Could not reach the server to import the spreadsheet.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) runImport(file)
  }

  const onConfirm = async () => {
    if (!result) return
    if (!title.trim()) {
      toast('Give the budget template a name.', { type: 'error' })
      return
    }
    if (duplicateTitle) {
      toast('A budget template with this name already exists.', { type: 'error' })
      return
    }
    // Keep the modal's state until the save resolves, so a failure leaves the preview on screen
    // to retry rather than dropping the admin back to an empty dialog.
    await onApply(title.trim(), result)
  }

  return (
    <Modal
      title="Import Budget Template from Spreadsheet"
      titleOkay={result ? 'Add Template' : ''}
      titleCancel="Close"
      isOpen={isOpen}
      lastUpdatedAt={Date.now()}
      loading={loading}
      disabled={!result || !title.trim() || duplicateTitle}
      onOk={onConfirm}
      onClose={handleClose}
    >
      <div className="w-[640px] max-w-full">
        <p className="text-sm text-gray-500 mb-4">
          Upload a budget or scope-of-work spreadsheet (.xlsx, .xls or .csv). Its section headings and line items
          become a new template. Amounts in the sheet are ignored — a template describes structure only.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={onFileChange}
        />

        <div className="flex items-center gap-3 mb-4">
          <Button onClick={() => inputRef.current?.click()} disabled={loading}>
            <div className="flex items-center">
              <DocumentArrowUpIcon className="h-5 w-5 mr-1" />
              <span>{fileName ? 'Choose a different file' : 'Choose spreadsheet'}</span>
            </div>
          </Button>
          {!!fileName && <span className="text-sm text-gray-600">{fileName}</span>}
        </div>

        {result && (
          <div>
            <div className="mb-4">
              <Input2
                title="Template Name"
                value={title}
                error={duplicateTitle ? 'A budget template with this name already exists.' : ''}
                onChange={(value: string) => setTitle(value)}
              />
            </div>

            <p className="text-sm text-gray-600 mb-2">
              Found <span className="font-semibold">{result.sections.length}</span> section
              {result.sections.length === 1 ? '' : 's'} and <span className="font-semibold">{lineCount}</span> line
              item{lineCount === 1 ? '' : 's'}.
              {result.skipped > 0 && ` ${result.skipped} unreadable row${result.skipped === 1 ? '' : 's'} skipped.`}
            </p>
            {result.source === 'spreadsheet' && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                The AI service was unavailable, so this was read from the spreadsheet's own layout. Section names and
                line items are the sheet's; set each section's cost type after adding it.
              </p>
            )}
            <p className="text-sm text-gray-500 mb-3">
              Review it below, then add it. You can edit the sections and line items afterwards.
            </p>

            <div className="max-h-[320px] overflow-y-auto border rounded">
              {result.sections.map((section) => (
                <div className="border-b last:border-b-0" key={section.value}>
                  <div className="bg-gray-50 px-3 py-2 text-sm font-semibold flex items-center justify-between">
                    <span>{section.label}</span>
                    <span className="text-xs font-normal text-gray-500">{section.defaultCostType}</span>
                  </div>
                  <ul className="px-3 py-2 text-sm text-gray-700">
                    {section.items.map((key) => (
                      <li className="py-[2px]" key={key}>
                        {result.itemMapping[key] || key}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
